// File for holding misc cognito related utility functions

import {
  USER_IS_CREW_MEMBER_KEY,
} from "../constants";
import {
  CookieStorage,
  CognitoUser,
  CognitoUserPool,
  CognitoRefreshToken,
} from "amazon-cognito-identity-js";
import { decodeJWT } from "../shared/utils";

import Cookies from "js-cookie";

/**
 * @typedef CognitoSession
 * @type {object}
 * @property {number} clockDrift
 * @property {string} idToken
 * @property {string} refreshToken
 * @property {string} LastAuthUser
 * @property {CognitoUserData} userData
 * @property {string} accessToken
 */

/**
 * @typedef CognitoUserData
 * @type {object}
 * @property {CognitoAttribute[]} UserAttributes
 * @property {string} Username - A UUID
 */

/**
 * @typedef CognitoAttribute
 * @type {object}
 * @property {string} Name
 * @property {string} Value
 */


/**
  @typedef {Object} CogIdToken
  @property {string} aud
  @property {number} auth_time
  @property {string} cognito
  @property {string} custom
  @property {boolean} custom
  @property {string} email
  @property {boolean} email_verified
  @property {string} event_id
  @property {number} exp
  @property {number} iat
  @property {string} iss
  @property {string} jti
  @property {string} origin_jti
  @property {string} phone_number
  @property {boolean} phone_number_verified
  @property {string} sub
  @property {string} token_use
  */

class CognitoUtils {
  /**
   * A synchronous method for getting the cognito authentication items from local storage (provided they exist)
   * @returns {CognitoSession}
   */
  static getCognitoAuth = () => {
    // Cognito util lib functions tend to be async, or take callbacks
    // This function allows us to synchrously retrive the items out of local storage without
    // trying to deal with async code, or those callbacks    
    const items = Cookies.get();
    const keys = Object.keys(items);

    const SUB_NAMES = {
      clockDrift: "clockDrift",
      idToken: "idToken",
      refreshToken: "refreshToken",
      userData: "userData",
      accessToken: "accessToken",
      LastAuthUser: "LastAuthUser",
    };

    const cognitoAuth = {
      clockDrift: 0,
      idToken: "",
      refreshToken: "",
      LastAuthUser: "", // In our case, this is an email address
      userData: {}, 
      accessToken: "",
    };

    keys.forEach((key) => {
      if (key.match(/CognitoIdentityServiceProvider/)) {
        const subName = key.split(".").pop();
        if (subName === SUB_NAMES.clockDrift) {
          cognitoAuth.clockDrift = items[key];
        } else if (subName === SUB_NAMES.idToken) {
          cognitoAuth.idToken = items[key];
        } else if (subName === SUB_NAMES.refreshToken) {
          cognitoAuth.refreshToken = items[key];
        } else if (subName === SUB_NAMES.LastAuthUser) {
          cognitoAuth.LastAuthUser = items[key];
        } else if (subName === SUB_NAMES.userData) {
          const userData = decodeURIComponent(items[key]);
          cognitoAuth.userData = JSON.parse(userData);
        } else if (subName === SUB_NAMES.accessToken) {
          cognitoAuth.accessToken = items[key];
        }
      }
    });
    return cognitoAuth;
  };

  static _getLocalStorageKeysAndItems = () => {
    const items = { ...window.localStorage };
    const keys = Object.keys(items);
    return { keys, items };
  };

  static setCognitoToken = (token) => {
    let key = this._createKeyPrefix(token);
    if (!key) {
      return false;
    }
    key += ".idToken";
    Cookies.set(key, token)

    return true;
  };

  /**
   * @param {Boolean} value
   */
  static setUserIsCrewMember = (value) =>
    window.localStorage.setItem(USER_IS_CREW_MEMBER_KEY, String(value));

  static getUserIsCrewMember = () =>
    window.localStorage.getItem(USER_IS_CREW_MEMBER_KEY) === "true";

  static _createKeyPrefix = (token) => {
    //JWTs come in three base64 encoded sections in a guaranteed order
    // 1) Header
    // 2) Payload
    // 3) signature
    // We need to pull the email from the payload for this
    const jwtSections = token.split(".");
    if (jwtSections.length !== 3) {
      return "";
    }
    const email = JSON.parse(atob(jwtSections[1])).email;
    return `CognitoIdentityServiceProvider.${CONFIG.COGNITO.Auth.userPoolWebClientId}.${email}`;
  };

  static _getCookieDomain = () => {
    let cookieDomain = "localhost";
    if (CONFIG.COOKIE_DOMAIN && CONFIG.COOKIE_DOMAIN != "") {
      cookieDomain = CONFIG.COOKIE_DOMAIN;
    }
    return cookieDomain;
  }

  /**
   * Build a CognitoUserPool object, using the current user pool config for web-angular
   * @returns {CognitoUserPool}
   */
  static _buildUserPool = () => {
    var poolData = {
      UserPoolId: CONFIG.COGNITO.Auth.userPoolId,
      ClientId: CONFIG.COGNITO.Auth.userPoolWebClientId,
      Storage: new CookieStorage({
        domain: this._getCookieDomain(),
      }), // Subdomains are included
    };
    return new CognitoUserPool(poolData);
  };

  /**
   * Build a CognitoUser object, using the current configured user pool
   * @param {string} username - The users email
   * @returns {CognitoUser}
   */
  static _buildUser = (username) => {
    var userData = {
      Username: username,
      Pool: this._buildUserPool(),
      Storage: new CookieStorage({
        domain: this._getCookieDomain(),
      }), // Subdomains are included
    };
    return new CognitoUser(userData);
  };

  /**
   * Refresh the users cognito authentication using username and refresh token.
   * Can be used to setup local storage with values in the way the cognito libs expect.
   * @param {string} username - The users email
   * @param {string} refreshToken
   * @param {(error: string) => void} handlerRefresh
   */
  static refreshCognitoAuthentication = (
    username,
    refreshToken,
    handleRefresh
  ) => {
    const cognitoUser = this._buildUser(username);
    // Re-auth with auth token to get id and access tokens
    const refresh_token = new CognitoRefreshToken({
      RefreshToken: refreshToken,
    });
    cognitoUser.refreshSession(refresh_token, handleRefresh);
  };

  /**
   * Try to refresh the user's current cognito session.
   * If the session is expired, a call to cognito will be triggered to refresh the session.
   *
   * NOTE: The user must aready have been authenticated for this to work, i.e. call
   * refreshCognitoAuthentication first, then call this later.
   */
  static refreshCognitoSessionIfExpired = () => {
    const sessionWasInvalid = !this.sessionIsValid();
    const { refreshToken , LastAuthUser: username } = this.getCognitoAuth();
    if (
      sessionWasInvalid &&
      // Don't refresh the window if user is trying to use old password reset page
      // Intended to be used by developers to get a password set in legacy
      !window.location.href.includes("/reset")
    ) {
      this.refreshCognitoAuthentication(username, refreshToken, () => {
        // Refresh the window if the last session was INVALID (had expired)
        // Sometimes the site can be stuck on a blank white page if we don't reload after refreshing
        window.location.reload();
      });
    }
  };

  /**
   * Checks to see if the session is still valid based on session expiry information found
   * in tokens and the current time (adjusted with clock drift)
   * This is basically copy-pasted from the library, but made into a synchronous function
   * @returns {boolean}
   */
  static sessionIsValid = () => {
    const now = Math.floor(new Date() / 1000);
    const cogAuth = this.getCognitoAuth();

    if (cogAuth.idToken === "") {
      console.error("User missing cognito idToken in cookies");
      return false;
    }

    /** @type {CogIdToken} */
    const idToken = decodeJWT(cogAuth.idToken);
    const clockDrift =
      cogAuth.clockDrift !== "" ? Number(cogAuth.clockDrift) : 0;
    const adjusted = now - clockDrift;

    // Crew member users ONLY pass in an idToken
    if (this.getUserIsCrewMember()) {
      return adjusted < idToken.exp;
    }

    if (cogAuth.accessToken === "") {
      console.error("User missing access token in cookies");
      return false;
    }

    const accessToken = decodeJWT(cogAuth.accessToken);

    return adjusted < accessToken.exp && adjusted < idToken.exp;
  };

  /**
   * @returns {string | null}
   */
  static getUserExternalId () {
    const userData = CognitoUtils.getCognitoAuth().userData;

    let userExternalId = null;
    userData.UserAttributes.forEach((attr) => {
      // If we made it here, web-angular is running locally, and web-react is not.
      // user external id won't be in local storage and we need it to be in order to make calls to api-v1 and api-v2, if _they_ are running locally
      if (attr.Name === "custom:external_user_id") {
        userExternalId = attr.Value;
      }
    });
    return userExternalId;
  }
}
export default CognitoUtils;
